import { formatUserFacingError } from '@/lib/errors'
import { useState, useEffect, useMemo, useCallback } from 'react'
import { adminMailingListService, type BulkAddResult } from '../services/admin.mailingList.service'
import type { MailingListMemberDocument, MailingListSource } from '@/types'
import type { MailingListMemberFormValues, MailingListFilters } from '@/features/mailing-list'
import type { ParsedEmailEntry } from '@/lib/csv'

const DEFAULT_FILTERS: MailingListFilters = {
  segments: [],
  sources: [],
  subscribedOnly: false,
}

export function useAdminMailingList() {
  const [members, setMembers] = useState<Array<MailingListMemberDocument & { id: string }>>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filters, setFilters] = useState<MailingListFilters>(DEFAULT_FILTERS)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await adminMailingListService.getAll()
      setMembers(data)
    } catch (err) {
      setError(formatUserFacingError(err, 'Failed to load mailing list.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const filtered = useMemo(() => {
    return members.filter((m) => {
      if (filters.subscribedOnly && !m.isSubscribed) return false
      if (
        filters.segments.length > 0 &&
        !filters.segments.some((seg) => m.segments.includes(seg))
      ) {
        return false
      }
      if (filters.sources.length > 0 && !filters.sources.includes(m.source)) {
        return false
      }
      return true
    })
  }, [members, filters])

  async function addMember(data: MailingListMemberFormValues): Promise<void> {
    await adminMailingListService.addMember(data, 'admin-manual')
    await load()
  }

  async function bulkAdd(entries: ParsedEmailEntry[], source: MailingListSource): Promise<BulkAddResult> {
    const result = await adminMailingListService.bulkAdd(entries, source)
    await load()
    return result
  }

  async function deleteMember(id: string): Promise<void> {
    await adminMailingListService.deleteMember(id)
    setMembers((prev) => prev.filter((m) => m.id !== id))
  }

  return {
    members,
    filtered,
    loading,
    error,
    filters,
    setFilters,
    addMember,
    bulkAdd,
    deleteMember,
    refresh: load,
  }
}
